import { useEffect, useState } from 'react';
import { useAuth } from 'oidc-react';
import { Database, Server, RefreshCw, Trash2, Layers, AlertTriangle, Inbox, Check } from 'lucide-react';
import { useCacheManagement } from '../../components/services/cacheManagementService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import { CACHE_PROJECTS, CacheProject } from './constants/cacheManagement.config';

type PrefixState = {
  loading: boolean;
  prefixes: string[];
  error: boolean;
};

type ConfirmAction = { type: 'flush' } | { type: 'clearAll'; project: CacheProject };

const CacheManagementTab = () => {
  const auth = useAuth();
  const { addToast } = useToast();
  const { getCachePrefixes, flushCache, clearCachePrefixes } = useCacheManagement();
  const role = auth?.userData?.profile?.role ?? '';
  const isGlobalAdmin = role === 'globaladmin';

  const [prefixState, setPrefixState] = useState<Record<string, PrefixState>>({});
  const [selectedPrefixes, setSelectedPrefixes] = useState<Record<string, Set<string>>>({});
  const [clearingProject, setClearingProject] = useState<string | null>(null);
  const [flushing, setFlushing] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirming, setConfirming] = useState(false);

  const loadPrefixes = async (project: string) => {
    setPrefixState((prev) => ({ ...prev, [project]: { loading: true, prefixes: prev[project]?.prefixes ?? [], error: false } }));

    try {
      const prefixes = await getCachePrefixes(project);
      setPrefixState((prev) => ({ ...prev, [project]: { loading: false, prefixes, error: false } }));
    } catch {
      setPrefixState((prev) => ({ ...prev, [project]: { loading: false, prefixes: [], error: true } }));
    }
  };

  useEffect(() => {
    if (!isGlobalAdmin) return;
    CACHE_PROJECTS.forEach((project) => loadPrefixes(project.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalAdmin]);

  const runFlush = async () => {
    setFlushing(true);

    try {
      // Flush is shared across every project's backend, so a single call is enough.
      await flushCache(CACHE_PROJECTS[0].name);
      addToast('Cache flushed successfully', 'success');
      CACHE_PROJECTS.forEach((project) => loadPrefixes(project.name));
    } catch {
      addToast('Failed to flush cache', 'error');
    } finally {
      setFlushing(false);
    }
  };

  const togglePrefix = (project: string, prefix: string) => {
    setSelectedPrefixes((prev) => {
      const current = new Set(prev[project] ?? []);
      if (current.has(prefix)) current.delete(prefix);
      else current.add(prefix);
      return { ...prev, [project]: current };
    });
  };

  const runClear = async (project: string, prefixesToClear: string[]) => {
    setClearingProject(project);

    try {
      await clearCachePrefixes(project, prefixesToClear);
      addToast('Cache cleared successfully', 'success');
      setSelectedPrefixes((prev) => ({ ...prev, [project]: new Set() }));
      await loadPrefixes(project);
    } catch {
      addToast('Failed to clear cache', 'error');
    } finally {
      setClearingProject(null);
    }
  };

  // Clear is per-project (unlike flush): if the user picked specific prefixes for this
  // project, only those are sent immediately; if none are selected the action clears
  // every prefix currently listed for the project, so that path is gated behind a
  // confirmation modal — same as Flush, which affects every project at once.
  const handleClear = (project: CacheProject) => {
    const state = prefixState[project.name];
    const selected = selectedPrefixes[project.name];

    if (selected && selected.size > 0) {
      runClear(project.name, Array.from(selected));
      return;
    }

    const allPrefixes = state?.prefixes ?? [];
    if (allPrefixes.length === 0) {
      addToast('No prefixes to clear', 'info');
      return;
    }

    setConfirmAction({ type: 'clearAll', project });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;

    setConfirming(true);
    try {
      if (confirmAction.type === 'flush') {
        await runFlush();
      } else {
        const allPrefixes = prefixState[confirmAction.project.name]?.prefixes ?? [];
        await runClear(confirmAction.project.name, allPrefixes);
      }
    } finally {
      setConfirming(false);
      setConfirmAction(null);
    }
  };

  const totalPrefixes = Object.values(prefixState).reduce((sum, s) => sum + (s?.prefixes.length ?? 0), 0);
  const loadedCount = Object.values(prefixState).filter((s) => s && !s.loading).length;

  if (!isGlobalAdmin) {
    return (
      <div className="rounded-xl border p-6" style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}>
        <h2 className="text-xl font-bold text-white mb-2">Cache Data</h2>
        <p className="text-sm text-gray-400 m-0">This action is available only for global administrators.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1080px] mx-auto text-white space-y-5">
      {/* Header */}
      <div
        className="flex flex-col md:flex-row md:items-center justify-between gap-5 p-5 rounded-xl border"
        style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Database size={15} className="text-gray-500 flex-shrink-0" />
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Cache Management</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white m-0">Cache Data</h2>
            <p className="text-sm text-gray-400 mt-1 max-w-[520px] leading-relaxed">
              Inspect cached prefixes across every project, or flush the shared cache in one go.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <span
              className="px-3 py-1 rounded-full text-xs font-medium border"
              style={{ backgroundColor: '#252525', borderColor: '#333', color: '#d7d7d7' }}
            >
              {CACHE_PROJECTS.length} projects
            </span>
            <span
              className="px-3 py-1 rounded-full text-xs font-medium border"
              style={{ backgroundColor: '#252525', borderColor: '#333', color: '#d7d7d7' }}
            >
              {loadedCount < CACHE_PROJECTS.length ? 'Loading prefixes…' : `${totalPrefixes} cached prefixes total`}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setConfirmAction({ type: 'flush' })}
          disabled={flushing}
          className="flex items-center justify-center gap-2 bg-[#D12929] hover:bg-[#b91f1f] text-white text-sm font-medium py-2.5 px-5 rounded-full transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Trash2 size={15} />
          {flushing ? 'Flushing…' : 'Flush Cache'}
        </button>
      </div>

      {/* Projects grid */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {CACHE_PROJECTS.map((project) => {
          const state = prefixState[project.name];

          return (
            <div
              key={project.name}
              className="rounded-xl border p-4 transition-colors hover:border-[#444]"
              style={{ backgroundColor: '#161617', borderColor: '#2a2a2a' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: '#252525' }}>
                    <Server size={14} className="text-gray-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate m-0">{project.label}</p>
                    <p className="text-[11px] text-gray-500 truncate m-0">{project.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => loadPrefixes(project.name)}
                  disabled={state?.loading}
                  title="Refresh prefixes"
                  className="flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-white hover:bg-[#252525] transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  <RefreshCw size={13} className={state?.loading ? 'animate-spin' : ''} />
                </button>
              </div>

              <div style={{ borderTop: '1px solid #232323' }} className="pt-3">
                {state?.loading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-xs">
                    <RefreshCw size={12} className="animate-spin" />
                    Loading prefixes…
                  </div>
                ) : state?.error ? (
                  <div className="flex items-center gap-2 text-xs" style={{ color: '#ef4444' }}>
                    <AlertTriangle size={12} />
                    Failed to load prefixes
                  </div>
                ) : state?.prefixes.length ? (
                  <>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {state.prefixes.map((prefix) => {
                        const isSelected = selectedPrefixes[project.name]?.has(prefix) ?? false;

                        return (
                          <button
                            key={prefix}
                            type="button"
                            onClick={() => togglePrefix(project.name, prefix)}
                            className="inline-flex items-center gap-1 rounded-full text-[11px] px-2.5 py-1 transition-colors"
                            style={
                              isSelected
                                ? { border: '1px solid #D12929', color: '#ff9d9d', backgroundColor: '#2A0B0B' }
                                : { border: '1px solid #333', color: '#d7d7d7', backgroundColor: '#1D1D1F' }
                            }
                          >
                            {isSelected ? <Check size={10} /> : <Layers size={10} className="text-gray-500" />}
                            {prefix}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={() => handleClear(project)}
                      disabled={clearingProject === project.name}
                      className="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderColor: '#3A3A3A', color: '#f0a0a0' }}
                    >
                      <Trash2 size={12} />
                      {clearingProject === project.name
                        ? 'Clearing…'
                        : selectedPrefixes[project.name]?.size
                          ? `Clear Selected (${selectedPrefixes[project.name]?.size})`
                          : 'Clear All'}
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-gray-600 text-xs">
                    <Inbox size={12} />
                    No cached prefixes
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmationModal
        show={confirmAction !== null}
        title={confirmAction?.type === 'flush' ? 'Flush All Caches' : `Clear All Cache — ${confirmAction?.project.label}`}
        description={
          confirmAction?.type === 'flush'
            ? 'This flushes the cache for every project, not just one.'
            : `This clears every cached prefix for ${confirmAction?.project.label} only. Other projects are not affected.`
        }
        warningTitle="Both Redis and in-memory caches will be removed"
        warningMessage={
          confirmAction?.type === 'flush'
            ? 'This action cannot be undone. All cached data across all projects — both Redis and in-memory — will be removed immediately.'
            : 'This action cannot be undone. All cached data for this project — both Redis and in-memory — will be removed immediately.'
        }
        confirmLabel={confirmAction?.type === 'flush' ? 'Flush Cache' : 'Clear All'}
        loading={confirming}
        onConfirm={handleConfirm}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
};

export default CacheManagementTab;
